import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  ShieldCheck, ShieldAlert, User as UserIcon, Globe, Clock, RefreshCw,
  CheckCircle2, XCircle, LogIn,
} from "lucide-react";
import { formatDateTimeIt } from "@/lib/format";

const METHOD_LABEL = { google: "Google", admin: "Admin" };

function methodBadgeClass(m) {
  if (m === "google") return "bg-blue-50 text-blue-700 border-blue-200";
  if (m === "admin") return "bg-stone-900 text-white border-stone-900";
  return "bg-stone-100 text-stone-600 border-stone-200";
}

function shortUA(ua) {
  if (!ua) return "—";
  // Extract browser + OS in a compact form
  const isMobile = /Mobile|Android|iPhone/i.test(ua);
  let browser = "Browser";
  if (/Firefox\//.test(ua)) browser = "Firefox";
  else if (/Edg\//.test(ua)) browser = "Edge";
  else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser = "Chrome";
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = "Safari";
  else if (/curl/i.test(ua)) browser = "curl";
  let os = "";
  if (/Windows/i.test(ua)) os = "Windows";
  else if (/Mac OS X/i.test(ua)) os = "macOS";
  else if (/iPhone|iPad|iOS/i.test(ua)) os = "iOS";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/Linux/i.test(ua)) os = "Linux";
  return `${browser}${os ? " · " + os : ""}${isMobile ? " · mobile" : ""}`;
}

const KpiCard = ({ icon: Icon, label, value, tone = "orange" }) => {
  const tones = {
    orange: "bg-[#F56A1F] text-white",
    dark: "bg-stone-900 text-white",
    emerald: "bg-emerald-600 text-white",
    red: "bg-red-500 text-white",
  };
  return (
    <Card className="p-0 overflow-hidden border-stone-200 shadow-sm rounded-2xl card-hover">
      <div className={`${tones[tone]} grain-orange px-5 py-3 flex items-center justify-between`}>
        <div className="text-[13px] font-medium tracking-wide">{label}</div>
        <Icon className="w-4 h-4 opacity-90" strokeWidth={1.75} />
      </div>
      <div className="p-6">
        <div className="text-4xl font-semibold text-stone-900 tracking-tighter">{value}</div>
      </div>
    </Card>
  );
};

export default function Accessi() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState("tutti");
  const [outcome, setOutcome] = useState("tutti"); // tutti | ok | ko

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (method !== "tutti") params.method = method;
      if (outcome === "ok") params.success = true;
      else if (outcome === "ko") params.success = false;
      const [logs, sum] = await Promise.all([
        api.get("/audit/logins", { params }),
        api.get("/audit/summary"),
      ]);
      setRows(logs.data);
      setSummary(sum.data);
    } finally {
      setLoading(false);
    }
  }, [method, outcome]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 lg:p-8 max-w-[1600px]">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.16em] text-[#F56A1F] font-semibold mb-2">
            Sicurezza
          </div>
          <h1 className="text-4xl font-semibold tracking-tight text-stone-900">Accessi</h1>
          <p className="text-stone-500 mt-1 max-w-2xl">
            Cronologia di ogni tentativo di accesso al CRM: chi è entrato, quando, da dove e con quale metodo.
          </p>
        </div>
        <Button onClick={load} variant="outline" className="gap-2 h-11">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        <KpiCard icon={LogIn} label="Accessi Totali" value={summary?.totale_accessi ?? "—"} tone="dark" />
        <KpiCard icon={ShieldCheck} label="Riusciti" value={summary?.riusciti ?? "—"} tone="emerald" />
        <KpiCard icon={ShieldAlert} label="Falliti" value={summary?.falliti ?? "—"} tone="red" />
        <KpiCard icon={Clock} label="Ultime 24 ore" value={summary?.ultime_24h ?? "—"} tone="orange" />
      </div>

      {/* Last successful login card */}
      {summary?.ultimo_accesso && (
        <Card className="border-stone-200 shadow-sm rounded-2xl overflow-hidden mb-6">
          <div className="grain-orange bg-[#F56A1F] px-5 py-3 flex items-center justify-between">
            <div className="text-white font-medium">Ultimo accesso riuscito</div>
          </div>
          <div className="p-6 flex items-center gap-6 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Data e ora</div>
              <div className="text-stone-900 font-medium">{formatDateTimeIt(summary.ultimo_accesso.timestamp)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Utente</div>
              <div className="text-stone-900 font-medium">
                {summary.ultimo_accesso.name || summary.ultimo_accesso.email}
              </div>
              <div className="text-xs text-stone-500">{summary.ultimo_accesso.email}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Metodo</div>
              <Badge variant="outline" className={`${methodBadgeClass(summary.ultimo_accesso.method)} text-xs`}>
                {METHOD_LABEL[summary.ultimo_accesso.method] || summary.ultimo_accesso.method}
              </Badge>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">IP</div>
              <div className="text-stone-900 font-mono text-sm">{summary.ultimo_accesso.ip || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Dispositivo</div>
              <div className="text-stone-900 text-sm">{shortUA(summary.ultimo_accesso.user_agent)}</div>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-stone-200 shadow-sm rounded-2xl p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-3">
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger data-testid="audit-filter-method" className="md:w-48 bg-white h-10">
              <SelectValue placeholder="Metodo" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="tutti">Tutti i metodi</SelectItem>
              <SelectItem value="google">Google</SelectItem>
              <SelectItem value="admin">Amministratore</SelectItem>
            </SelectContent>
          </Select>
          <Select value={outcome} onValueChange={setOutcome}>
            <SelectTrigger data-testid="audit-filter-outcome" className="md:w-48 bg-white h-10">
              <SelectValue placeholder="Esito" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="tutti">Tutti gli esiti</SelectItem>
              <SelectItem value="ok">Solo riusciti</SelectItem>
              <SelectItem value="ko">Solo falliti</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-sm text-stone-500 ml-auto self-center">
            {rows.length} {rows.length === 1 ? "voce" : "voci"} mostrate
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="border-stone-200 shadow-sm rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50/70 border-b border-stone-200">
              <tr className="text-left text-[11px] uppercase tracking-widest text-stone-500 font-semibold">
                <th className="px-6 py-3">Data e Ora</th>
                <th className="px-6 py-3">Utente</th>
                <th className="px-6 py-3">Metodo</th>
                <th className="px-6 py-3">Esito</th>
                <th className="px-6 py-3">IP</th>
                <th className="px-6 py-3">Dispositivo</th>
                <th className="px-6 py-3">Dettaglio</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-stone-400">Caricamento…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-stone-400">Nessun accesso registrato.</td></tr>
              ) : rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`audit-row-${r.id}`}
                  className="border-b border-stone-100 hover:bg-stone-50/60 transition-colors"
                >
                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className="text-stone-900 font-medium">{formatDateTimeIt(r.timestamp)}</div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <UserIcon className="w-3.5 h-3.5 text-stone-400 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-stone-900 truncate max-w-[240px]">
                          {r.name || r.email || "—"}
                        </div>
                        {r.name && r.email && r.name.toLowerCase() !== r.email && (
                          <div className="text-[11px] text-stone-500 truncate max-w-[240px]">{r.email}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <Badge variant="outline" className={`${methodBadgeClass(r.method)} text-xs`}>
                      {METHOD_LABEL[r.method] || r.method}
                    </Badge>
                  </td>
                  <td className="px-6 py-3">
                    {r.success ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Riuscito
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium">
                        <XCircle className="w-3.5 h-3.5" /> Fallito
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-1 text-stone-700 text-xs font-mono">
                      <Globe className="w-3 h-3 text-stone-400" />
                      {r.ip || "—"}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-stone-600 text-xs">{shortUA(r.user_agent)}</td>
                  <td className="px-6 py-3 text-stone-500 text-xs">
                    {r.detail ? (
                      <span className={r.success ? "text-stone-500" : "text-red-600"}>
                        {r.detail}
                      </span>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
